import { useQueryClient } from '@tanstack/react-query';
import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
} from 'react';
import type { DatovkyApiSeznam } from '@gov-nx/api';
import {
	searchDataBoxesQuery,
	sendSubmissionQuery,
	useAxiosInstance,
	usePoMutation,
	usePoQuery,
} from '@gov-nx/api';
import { useExtraConfigContext } from '@gov-nx/core/app';
import { useDownloadEvents, useMessageEvents } from '@gov-nx/core/events';
import type { WizardFormStep } from '@gov-nx/core/hooks';
import {
	useIdentityDocumentLoad,
	usePoIndividualDataLoad,
	useProcessControl,
	useServiceAuthorization,
	useTreeFormCheckboxes,
	useWizard,
} from '@gov-nx/core/hooks';
import { getRequiredDataBoxes, useLocale } from '@gov-nx/core/service';
import type { Nullable } from '@gov-nx/core/types';
import type { ServiceCode } from '../definitions/codes';
import {
	FormInstanceStep2,
	FormInstanceStep3,
	FormInstanceStep4,
	prepareSubmitData,
	scopeWithLabels,
	useFormTree,
} from './FormDefinitions';
import type {
	FormData,
	FormDataStep2,
	FormDataStep3,
	ServiceContextTypes,
	ServiceControls,
} from './service.types';
import { useAutorizaceContext } from 'libs/component/web/src/Autorizace/Context';

export const ServiceContext =
	createContext<Nullable<ServiceContextTypes>>(null);

export interface ServiceContextProviderProps {
	children: React.ReactNode;
	code: ServiceCode;
}

const AUTHORIZATION_SERVICE_NAME = 'PODANI_ZR10_POSKYTNUTI';

export function ZadostOPoskytnutiUdajuContextProvider({
	children,
	code,
}: ServiceContextProviderProps) {
	const { axiosInstance } = useAxiosInstance();
	const { toastMessageSuccess } = useMessageEvents();
	const { t } = useLocale(code);
	const authContext = useAutorizaceContext();
	const {
		extraConfig: { application },
	} = useExtraConfigContext();
	const requiredDataBoxes = getRequiredDataBoxes(code, application);
	const { setControls, controls } = useProcessControl<ServiceControls>({
		displayComplaintModal: false,
		isDownloadLoading: false,
		downloadError: null,
	});
	const { downloadBlob } = useDownloadEvents();
	const { individualPerson } = usePoIndividualDataLoad({
		onError: (initialError) => setControls({ initialError }),
	});
	const { getMainDocument, documents } = useIdentityDocumentLoad({
		onError: (initialError) => setControls({ initialError }),
	});

	const mainDocument = getMainDocument();
	useEffect(() => {
		if (individualPerson && documents && controls.initialLoading) {
			setControls({ initialLoading: false });
		}
	}, [individualPerson, documents, controls.initialLoading, setControls]);

	const queryClient = useQueryClient();

	const submitMutation = usePoMutation({
		mutationFn: async (data: FormData) => {
			const prepared = prepareSubmitData(
				mainDocument,
				lookUpQuery.data?.seznam?.[0]
			)(data);
			return sendSubmissionQuery(axiosInstance)(prepared);
		},
		onError: (error) => {
			setControls({ processError: error, processLoading: false });
			wizard.resetForms();
			treeCheckboxes.reset();
		},
		onSuccess: async (data) => {
			const nonHighAuthorizationFlow = () => {
				toastMessageSuccess(t('formular.zprava.odeslano'));
			};

			setControls({ processLoading: false });
			await queryClient.invalidateQueries({ queryKey: ['souhlasy'] });
			wizard.resetForms();
			treeCheckboxes.reset();

			if (authContext) {
				authContext?.onInitAuthorization(
					AUTHORIZATION_SERVICE_NAME,
					data.headers['x-autorizaceid'],
					nonHighAuthorizationFlow
				);
			} else {
				nonHighAuthorizationFlow();
			}
		},
	});

	const { isLowAuthorization, isAuthorizationEnforced } =
		useServiceAuthorization(AUTHORIZATION_SERVICE_NAME);

	const onSubmit = useCallback(
		async (values: FormData) => {
			setControls({ processError: null, processLoading: true });
			submitMutation.mutate(values);
		},
		[setControls, submitMutation]
	);

	const formStep2 = FormInstanceStep2({
		code,
		isDisabled: controls.processLoading,
	});

	const wizard = useWizard({
		steps: [
			{},
			formStep2.formDefinition,
			FormInstanceStep3(),
			FormInstanceStep4({
				dataBoxTypes: requiredDataBoxes,
				isLowAuthorization,
			}),
		],
		onSubmit,
	});

	const step2 = wizard.step(1) as unknown as WizardFormStep<FormDataStep2>;
	const personType = step2.formDefinition.formMethods.watch('typOsoby');
	useEffect(() => {
		if (personType === 'FO') {
			step2.formDefinition.formMethods.reset({
				...step2.formDefinition.formMethods.control._defaultValues,
				typOsoby: 'FO',
			});
		}
		if (personType === 'PO') {
			step2.formDefinition.formMethods.reset({
				...step2.formDefinition.formMethods.control._defaultValues,
				typOsoby: 'PO',
			});
		}
	}, [personType, step2.formDefinition.formMethods]);

	const typeSearch = 'GENERAL';
	const ds = step2.formDefinition.formMethods.watch('foDs');
	const lookUpQuery = usePoQuery<DatovkyApiSeznam>({
		queryKey: [
			'data-box-autocomplete',
			typeSearch,
			requiredDataBoxes[0],
			ds?.selected?.datovaSchrankaId,
		],
		queryFn: () =>
			searchDataBoxesQuery(
				axiosInstance,
				ds?.selected?.datovaSchrankaId || '',
				typeSearch,
				requiredDataBoxes[0]
			),
		enabled: !!ds?.selected,
		retry: 0,
		refetchOnWindowFocus: false,
	});

	const treeCheckboxes = useTreeFormCheckboxes({
		tree: useFormTree(),
		formMethods: (wizard.step(2) as unknown as WizardFormStep<FormDataStep3>)
			.formDefinition.formMethods,
	});

	const handleDownload = async () => {
		setControls({
			downloadError: null,
			isDownloadLoading: true,
		});
		return downloadBlob({
			data: prepareSubmitData(
				mainDocument,
				lookUpQuery.data?.seznam?.[0]
			)(wizard.formData),
			onSuccess: () => {
				setControls({ isDownloadLoading: false });
				wizard.resetForms();
			},
			onError: (error) => {
				setControls({ downloadError: error, isDownloadLoading: false });
				wizard.resetForms();
			},
		});
	};

	return (
		<ServiceContext.Provider
			value={{
				code,
				requiredDataBoxes,
				controls,
				setControls,
				individualPerson,
				isLowAuthorization,
				isAuthorizationEnforced,
				mainDocument,
				foDs: lookUpQuery.data?.seznam?.[0],
				wizard,
				formStep2,
				treeCheckboxes,
				scope: scopeWithLabels(treeCheckboxes.getTree()),
				handleDownload,
			}}>
			{children}
		</ServiceContext.Provider>
	);
}

export const useZadostOPoskytnutiUdajuContext = () =>
	useContext(ServiceContext) as ServiceContextTypes;
